<template>
  <div class="fans-page">
    <el-card shadow="never" class="search-card">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="用户ID">
          <el-input
            v-model="searchForm.userCode"
            placeholder="请输入用户ID"
            clearable
            style="width: 180px"
          />
        </el-form-item>
        <el-form-item label="用户手机号">
          <el-input
            v-model="searchForm.userPhone"
            placeholder="请输入手机号"
            clearable
            maxlength="11"
            style="width: 180px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :icon="Search" @click="handleQuery">查询</el-button>
          <el-button :icon="Refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card shadow="never" class="table-card">
      <el-table
        :key="tableKey"
        :data="list"
        v-loading="loading"
        border
        style="width: 100%"
        row-key="userCode"
        lazy
        :load="loadBoundUsers"
        :max-height="tableMaxHeight"
        :tree-props="{ hasChildren: 'hasChildren', children: 'children' }"
      >
        <el-table-column label="序号" width="60" align="center">
          <template #default="{ row, $index }">{{ getDisplayIndex(row, $index) }}</template>
        </el-table-column>
        <el-table-column prop="userCode" label="用户ID" min-width="260" align="center">
          <template #default="{ row }">{{ displayValue(row.userCode) }}</template>
        </el-table-column>
        <el-table-column prop="firstLevelNickname" label="一级用户昵称" min-width="160" align="center" show-overflow-tooltip>
          <template #default="{ row }">{{ row.isBoundUser ? '-' : displayValue(row.firstLevelNickname) }}</template>
        </el-table-column>
        <el-table-column prop="userPhone" label="用户手机号" width="150" align="center">
          <template #default="{ row }">{{ displayValue(row.userPhone) }}</template>
        </el-table-column>
        <el-table-column prop="secondLevelNickname" label="二级用户昵称" min-width="160" align="center" show-overflow-tooltip>
          <template #default="{ row }">{{ row.isBoundUser ? displayValue(row.secondLevelNickname) : '-' }}</template>
        </el-table-column>
        <el-table-column prop="bindTime" label="绑定时间" width="180" align="center">
          <template #default="{ row }">{{ row.isBoundUser ? displayValue(row.bindTime) : '-' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="120" align="center" fixed="right">
          <template #default="{ row }">
            <el-button
              v-if="row.isBoundUser"
              type="primary"
              link
              size="small"
              @click="openSubordinateDialog(row)"
            >
              查看下级
            </el-button>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>

    <!-- 查看下级弹窗 -->
    <el-dialog
      v-model="subordinateDialogVisible"
      title="下级用户"
      width="700px"
      destroy-on-close
      @closed="subordinateDialogList = []"
    >
      <div v-loading="subordinateDialogLoading" class="subordinate-dialog-body">
        <el-table :data="subordinateDialogList" border size="small" max-height="400">
          <el-table-column type="index" label="序号" width="56" align="center" :index="(i) => i + 1" />
          <el-table-column prop="userCode" label="用户ID" width="100" align="center">
            <template #default="{ row: r }">{{ displayValue(r.userCode ?? r.userId) }}</template>
          </el-table-column>
          <el-table-column prop="nickname" label="用户昵称" min-width="100" align="center" show-overflow-tooltip>
            <template #default="{ row: r }">{{ displayValue(r.nickname) }}</template>
          </el-table-column>
          <el-table-column prop="userPhone" label="用户手机号" width="120" align="center">
            <template #default="{ row: r }">{{ displayValue(r.userPhone) }}</template>
          </el-table-column>
          <el-table-column prop="bindTime" label="绑定时间" width="170" align="center">
            <template #default="{ row: r }">{{ displayValue(r.bindTime) }}</template>
          </el-table-column>
        </el-table>
        <div v-if="!subordinateDialogLoading && !subordinateDialogList.length" class="subordinate-empty">暂无下级数据</div>
      </div>
    </el-dialog>

      <div class="pagination-wrap">
        <el-pagination
          v-model:current-page="queryParams.pageIndex"
          v-model:page-size="queryParams.pageSize"
          layout="prev, next"
          :total="total"
          @current-change="getList"
        />
      </div>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, watch } from 'vue'
import { useCurrentAppInstance } from '@zto/zpage'
import { Search, Refresh } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

const app = useCurrentAppInstance()
const route = app.useRoute()

function readPhoneFromQuery () {
  const q = route.query.userPhone
  if (typeof q === 'string') return q.trim()
  if (Array.isArray(q) && q[0]) return String(q[0]).trim()
  return ''
}

function applyPhoneFromRoute () {
  const phone = readPhoneFromQuery()
  if (phone) searchForm.userPhone = phone
}

const searchForm = reactive({
  userCode: '',
  userPhone: ''
})
const loading = ref(false)
const list = ref<any[]>([])
const tableKey = ref(0)
const tableMaxHeight = 'calc(100vh - 320px)'
const queryParams = reactive({
  pageIndex: 1,
  pageSize: 10
})
const total = ref(0)
const boundListMap = ref<Record<string, any[]>>({})
const boundLoading = ref<Record<string, boolean>>({})

const subordinateDialogVisible = ref(false)
const subordinateDialogLoading = ref(false)
const subordinateDialogList = ref<any[]>([])

function buildParams () {
  return {
    userCode: (searchForm.userCode || '').trim() || undefined,
    userPhone: (searchForm.userPhone || '').trim() || undefined,
    pageNum: queryParams.pageIndex,
    pageSize: queryParams.pageSize
  }
}

function normalizeTopLevelRow(row: any) {
  return {
    ...row,
    firstLevelNickname: row?.nickname ?? '--',
    secondLevelNickname: '',
    hasChildren: true,
    bindTime: row?.bindTime ?? '--',
    isBoundUser: false
  }
}

function normalizeBoundRow(row: any) {
  return {
    ...row,
    userCode: row?.userCode ?? row?.userId ?? '--',
    firstLevelNickname: '',
    secondLevelNickname: row?.nickname ?? '--',
    userPhone: row?.userPhone ?? '--',
    bindTime: row?.bindTime ?? '--',
    hasChildren: false,
    isBoundUser: true
  }
}

function displayValue(value: any) {
  if (value === null || value === undefined || value === '') return '-'
  return value
}

function getDisplayIndex(row: any, index: number) {
  if (row?.isBoundUser) return '-'
  const topLevelIndex = list.value.findIndex(item => item.userCode === row?.userCode)
  const currentIndex = topLevelIndex > -1 ? topLevelIndex : index
  return (queryParams.pageIndex - 1) * queryParams.pageSize + currentIndex + 1
}

function resetExpandedState () {
  tableKey.value += 1
}

async function fetchList () {
  loading.value = true
  try {
    const params = buildParams()
    const res: any = await app.request({
      api: '/admin/dataversion/userList',
      method: 'POST',
      data: params
    })
    if (res && typeof res === 'object') {
      list.value = Array.isArray(res.list) ? res.list.map(normalizeTopLevelRow) : []
      total.value = 100000
    } else {
      list.value = []
      total.value = 0
    }
  } catch (e) {
    console.error('userList error', e)
    list.value = []

  } finally {

    loading.value = false
  }
}

async function fetchBoundUserList (userCode: string) {
  if (!userCode) return []
  boundLoading.value = { ...boundLoading.value, [userCode]: true }
  try {
    const res: any = await app.request({
      api: '/admin/dataversion/boundUserList',
      method: 'POST',
      data: { userCode }
    })
    const source = res?.list && Array.isArray(res.list) ? res.list : []
    const arr = source.map(normalizeBoundRow)
    boundListMap.value = { ...boundListMap.value, [userCode]: arr }
    if (!arr.length) {
      ElMessage.info('暂无下级数据')
    }
    return arr
  } catch (e) {
    console.error('boundUserList error', e)
    boundListMap.value = { ...boundListMap.value, [userCode]: [] }
    ElMessage.info('暂无下级数据')
    return []
  } finally {
    boundLoading.value = { ...boundLoading.value, [userCode]: false }
  }
}

function getList() {
  fetchList()
}

async function loadBoundUsers (row: any, _treeNode: unknown, resolve: (data: any[]) => void) {
  const code = row?.userCode
  if (!code) {
    resolve([])
    return
  }
  if (boundListMap.value[code] !== undefined) {
    resolve(boundListMap.value[code] || [])
    return
  }
  const children = await fetchBoundUserList(code)
  resolve(children)
}

/** 二级行「查看下级」：弹窗内调用 boundUserList 展示该用户的下级列表 */
function getRowUserCode (row: any) {
  return row?.userCode ?? (row?.userId != null ? String(row.userId) : '')
}

async function openSubordinateDialog (row: any) {
  const userCode = getRowUserCode(row)
  if (!userCode) return
  subordinateDialogVisible.value = true
  subordinateDialogLoading.value = true
  subordinateDialogList.value = []
  try {
    const arr = await fetchBoundUserList(userCode)
    subordinateDialogList.value = Array.isArray(arr) ? arr : []
  } catch (e) {
    console.error('boundUserList (dialog) error', e)
    subordinateDialogList.value = []
  } finally {
    subordinateDialogLoading.value = false
  }
}

function handleQuery () {
  queryParams.pageIndex = 1
  boundListMap.value = {}
  boundLoading.value = {}
  resetExpandedState()
  getList()
}

onMounted(() => {
  applyPhoneFromRoute()
  getList()
})

function handleReset () {
  searchForm.userCode = ''
  searchForm.userPhone = ''
  queryParams.pageIndex = 1
  boundListMap.value = {}
  boundLoading.value = {}
  resetExpandedState()
  getList()
}

watch(
  () => route.query.userPhone,
  () => {
    applyPhoneFromRoute()
    queryParams.pageIndex = 1
    boundListMap.value = {}
    boundLoading.value = {}
    resetExpandedState()
    getList()
  }
)
</script>

<style lang="scss" scoped>
.fans-page {
  padding: 16px 24px;
  background: #f5f7fa;
  min-height: 100%;
}

.search-card {
  margin-bottom: 20px;
  border-radius: 8px;

  .search-form {
    margin-bottom: 0;

    :deep(.el-form-item) {
      margin-right: 28px;
      margin-bottom: 0;
    }
  }
}

.table-card {
  border-radius: 8px;

  :deep(.el-table) {
    .el-table__cell {
      padding: 12px 0;
    }
  }

  :deep(.el-table__inner-wrapper::before) {
    display: none;
  }

  :deep(.el-table__header-wrapper) {
    position: sticky;
    top: 0;
    z-index: 3;
  }

  :deep(.el-table__placeholder) {
    display: none;
  }
}

.pagination-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.subordinate-dialog-body {
  min-height: 120px;
}

.subordinate-empty {
  color: #909399;
  font-size: 14px;
  padding: 24px 0;
  text-align: center;
}
</style>
